import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { BehaviorSubject, Subject, combineLatest, first, map, startWith, takeUntil } from 'rxjs';
import { PathPayload } from 'src/app/model/path.model';
import { ProcessType } from 'src/app/model/process-type.model';
import { AgentSaveCalculationPayload } from 'src/app/model/save-calculation.model';
import { FormsService } from 'src/app/services/forms.service';
import { ProcessService } from 'src/app/services/process.service';
import { AppState } from 'src/app/store';
import * as fromProcess from '../../store/process/process.reducer';
import isAppAgentOrTravelAgency from '../../utils/is-agent-or-travel-agency.util';
import { ApiState } from '../../model/api-state.model';

const DELAY_BEFORE_PROPER_VALUES = 10;
const CONCLUSION_STEP_NUMBER = 6;

@Component({
  selector: 'app-navigation-row',
  templateUrl: './navigation-row.component.html',
  styleUrls: ['./navigation-row.component.scss'],
})
export class NavigationRowComponent implements AfterViewInit, OnInit, OnDestroy {
  @ViewChild('navigationRow') navigationRow!: ElementRef<HTMLElement>;
  @Output() emitGoToNextStep = new EventEmitter();
  @Output() emitComponentLoaded = new EventEmitter<ElementRef<HTMLElement>>();
  @Input() offerValue?: number | null;
  @Input() processOfferVisible?: boolean | null;
  @Input() buttonLabel?: string | null;

  isAppAgentOrTravelAgency = isAppAgentOrTravelAgency();
  processKind$ = this.store.select(fromProcess.selectProcessKind);
  selectedPackage$ = this.store.select(fromProcess.selectProcessPackageName);
  addons$ = this.store.select(fromProcess.selectProcessAddons);
  selectedAgentSection$ = this.store.select(fromProcess.selectAgentSection);
  discountCode$ = this.store.select(fromProcess.selectOfferDiscountCode);
  sendBackOfficeData$ = this.store.select(fromProcess.selectSendBackOfficeData);
  currentStep$ = this.store.select(fromProcess.selectProcessStep);
  selectProcessSavedOfferError$ = this.store.select(fromProcess.selectProcessSavedOfferError);
  activateButton$ = combineLatest([this.selectProcessSavedOfferError$, this.currentStep$]);
  selectKnfLimitState$ = this.store.select(fromProcess.selectKnfLimitState);
  selectPremiumCalculationState$ = this.store.select(fromProcess.selectPremiumCalculationState);
  selectBlockListState$ = this.store.select(fromProcess.selectBlockListState);
  disableButton$ = new BehaviorSubject(false);
  ngDestroy$ = new Subject<void>();
  isAnyStatusPending$ = combineLatest([
    this.formsService.travelInPl.statusChanges.pipe(startWith(null)),
    this.formsService.tripAbroad.statusChanges.pipe(startWith(null)),
    this.formsService.resignation.statusChanges.pipe(startWith(null)),
    this.formsService.policyholder.statusChanges.pipe(startWith(null)),
    this.selectKnfLimitState$,
    this.selectPremiumCalculationState$,
    this.selectBlockListState$,
  ]).pipe(
    map(
      ([
        travelInPlStatus,
        tripAbroadStatus,
        resignationStatus,
        policyholderStatus,
        knfLimitState,
        premiumCalculationState,
        blockListState,
      ]) =>
        travelInPlStatus === 'PENDING' ||
        tripAbroadStatus === 'PENDING' ||
        resignationStatus === 'PENDING' ||
        policyholderStatus === 'PENDING' ||
        knfLimitState === ApiState.pending ||
        blockListState === ApiState.pending ||
        premiumCalculationState === ApiState.pending,
    ),
  );
  constructor(
    private store: Store<AppState>,
    private router: Router,
    private processService: ProcessService,
    private formsService: FormsService,
  ) {}

  goToNextStep() {
    this.emitGoToNextStep.emit();
  }

  saveOfferCalculation() {
    this.disableButton$.next(true);

    combineLatest([
      this.processKind$,
      this.selectedPackage$,
      this.addons$,
      this.selectedAgentSection$,
      this.discountCode$,
      this.sendBackOfficeData$,
    ])
      .pipe(first())
      .subscribe(([processKind, packageCodeName, selectedAddons, selectedAgentSection, discountCode, sendBackOfficeData]) => {
        let path: PathPayload;

        switch (processKind) {
          case ProcessType.TRIPABROAD:
            path = {
              tripAbroad: this.formsService.tripAbroadApi,
            };
            break;
          case ProcessType.TRAVELINPL:
            path = {
              travelInPL: this.formsService.travelInPlApi,
            };
            break;
          case ProcessType.RESIGNATION:
            path = {
              resignation: this.formsService.resignationApi,
            };
            break;
          default:
            throw new Error('Unknown process kind');
        }

        const agreements: number[] = [];

        const marketing = this.formsService.agreementsForm.controls.marketing.getRawValue();
        const statements = this.formsService.agreementsForm.controls.statements.getRawValue();

        Object.entries(marketing)
          .filter(([_, value]) => value)
          .forEach(([key]) => {
            const agreementsId = Number(key.replace('agreement.', ''));

            agreements.push(agreementsId);
          });

        Object.entries(statements)
          .filter(([_, value]) => value)
          .forEach(([key]) => {
            const agreementsId = Number(key.replace('agreement.', ''));

            agreements.push(agreementsId);
          });

        const payload: AgentSaveCalculationPayload = {
          path,
          policyholder: this.formsService.policyholderApi,
          insureds: this.formsService.insuredsApi,
          backofficeSection: sendBackOfficeData ? this.formsService.backOfficeSectionApi : null,
          selectedOffert: {
            packageCodeName,
            addOns: selectedAddons ?? {},
          },
          ...this.processService.apiPayload,
          discountCode: {
            code: discountCode || '',
          },
          ...(!(selectedAgentSection.premiumPercentIncrease === undefined || selectedAgentSection.premiumPercentIncrease === null)
            ? {
                agentSection: selectedAgentSection,
              }
            : {}),

          agreements,
          persist: true,
        };

        this.store.dispatch(fromProcess.actions.saveOfferCalculationStart({ payload }));
      });
  }

  ngOnInit(): void {
    this.activateButton$.pipe(takeUntil(this.ngDestroy$)).subscribe(([error, step]) => {
      if (error || step !== CONCLUSION_STEP_NUMBER) {
        this.disableButton$.next(false);
      }
    });
  }

  ngOnDestroy(): void {
    this.ngDestroy$.next();
    this.ngDestroy$.complete();
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.emitComponentLoaded.emit(this.navigationRow);
    }, DELAY_BEFORE_PROPER_VALUES);
  }
}
